import type { ChatTranscriptEntry } from '@matter_extractor/shared';
import {
  AFTER_CLOSURE_LABEL,
  ARRIVAL_ORDER_NOTE,
  EMPTY_TRANSCRIPT_LABEL,
  ROLE_LABEL,
  TRANSCRIPT_HEADING,
  breakLabel,
  lateStampsLabel,
  timeLabel,
} from './entretien-labels';

/**
 * S10's thread: one interview, in the order it ARRIVED (chat spec §8.2, §12).
 *
 * ## This is a reading instrument, not a chat window
 *
 * The public conversation of S3/S4 draws bubbles, because two people are talking
 * and each needs to see whose turn it is. Here nobody is talking: what the cycle
 * reads in a transcript is nammed in §1 - « par quoi il commence, en combien de
 * tours la première matière arrive, à quel rythme il monte en profondeur, et ce
 * qu'il fait quand ça bloque » - and every one of those four is a question about
 * RHYTHM. So the layout is a three-column ledger with the hour first: the eye
 * runs down the stamps, sees the four minutes between a question and its answer,
 * and reads the hesitation the bubbles would have hidden.
 *
 * ## The order, and why nothing here re-sorts
 *
 * The entries arrive in the order of arrival (`seq` ascending) and are rendered
 * in it, untouched. Rendering a message at its place of WRITING would show a
 * conversation nobody lived, and a re-read would draw conclusions about a
 * sequence that never happened (§8.2). The note in the header says so out loud,
 * because a reader of a transcript has no other way to know which of the two
 * orders he is looking at.
 *
 * ## What a traced break is
 *
 * A message whose two stamps diverge is the one observable trace of a cut
 * connection this repo stores: the client queue holds a message while the socket
 * is down and posts it at the reconnection, so the gap IS the length of the
 * break (§8). The separator is drawn BEFORE the message that carries the gap,
 * and that message shows both its hours - the only place `composedAt` earns a
 * place on a screen.
 */

export interface TranscriptReaderProps {
  entries: readonly ChatTranscriptEntry[];
}

export function TranscriptReader({ entries }: TranscriptReaderProps) {
  return (
    <section
      data-testid="chat-transcript"
      className="flex flex-col overflow-hidden rounded-md border border-gray-200 bg-white"
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-gray-100 border-b bg-gray-50 px-5 py-3">
        <h2 className="font-semibold text-gray-600 text-caption uppercase tracking-wide">
          {TRANSCRIPT_HEADING}
        </h2>
        {/* Said on the screen and repeated in the exported markdown: the paste
            outlives the tab, and both readers have to know the order is the
            order of arrival. */}
        <p className="text-gray-500 text-caption">{ARRIVAL_ORDER_NOTE}</p>
      </header>
      {entries.length === 0 ? (
        <p data-testid="chat-transcript-empty" className="px-5 py-8 text-gray-600 text-small">
          {EMPTY_TRANSCRIPT_LABEL}
        </p>
      ) : (
        <ol className="divide-y divide-gray-100">
          {entries.map((entry) => (
            <Entry key={entry.seq} entry={entry} />
          ))}
        </ol>
      )}
    </section>
  );
}

function Entry({ entry }: { entry: ChatTranscriptEntry }) {
  const late = entry.lateByMs !== null;
  return (
    <li
      data-testid="chat-transcript-entry"
      /*
        Zebra by ROLE and not by parity, which is what makes the two sides
        legible while scanning: the interviewer's lines sit on white, the
        guest's on `gray-50`. Parity striping would put a role on either ground
        as soon as somebody sends two lines in a row - and §9 says they will,
        which is precisely why `turns` could not hold this conversation.
      */
      className={entry.role === 'guest' ? 'bg-gray-50' : ''}
    >
      {late && entry.lateByMs !== null ? (
        <p
          data-testid="chat-transcript-break"
          className="flex items-center gap-3 px-5 pt-3 font-semibold text-jaune-deep text-caption uppercase tracking-wide"
        >
          <span aria-hidden="true" className="h-px flex-1 bg-gray-100" />
          {breakLabel(entry.lateByMs)}
          <span aria-hidden="true" className="h-px flex-1 bg-gray-100" />
        </p>
      ) : null}
      <div className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-3 px-5 py-3 sm:grid-cols-[3.5rem_7rem_minmax(0,1fr)] sm:gap-4">
        {/*
          The arrival hour, FIRST and in `tabular-nums`, because the column of
          stamps is what the rhythm is read down. `<time>` so the machine-
          readable value stays on the page even though the eye gets minutes.
        */}
        <time dateTime={entry.receivedAt} className="text-gray-500 text-caption tabular-nums">
          {timeLabel(entry.receivedAt)}
        </time>
        <p
          className={`text-caption sm:text-small ${
            entry.role === 'interviewer' ? 'font-semibold text-bleu-nuit-deep' : 'text-gray-700'
          }`}
        >
          {ROLE_LABEL[entry.role]}
        </p>
        {/*
          `whitespace-pre-wrap`: a verbatim carries its own line breaks, and
          collapsing them would reshape somebody's answer. `break-words`
          because a pasted URL is not a reason to make the page scroll sideways.
        */}
        <div className="col-span-2 min-w-0 space-y-1 sm:col-span-1">
          <p className="whitespace-pre-wrap break-words text-gray-900">{entry.text}</p>
          {late && entry.lateByMs !== null ? (
            <p
              data-testid="chat-transcript-late"
              className="font-semibold text-jaune-deep text-caption"
            >
              {lateStampsLabel(entry.composedAt, entry.receivedAt)}
            </p>
          ) : null}
          {entry.afterClosure ? (
            <p
              data-testid="chat-transcript-after-closure"
              className="font-semibold text-jaune-deep text-caption"
            >
              {AFTER_CLOSURE_LABEL}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}
